import { useCallback, useEffect, useRef, useState } from "react"
import {
	Bold,
	Italic,
	Link2,
	List,
	ListOrdered,
	Loader2,
	Paperclip,
	Underline,
	X
} from "lucide-react"
import {
	BODY_LIMIT,
	mediaUrl,
	stripMediaTokens,
	uploadFile,
	withMediaTokens
} from "../../api/client"
import { useApp } from "../../state/AppState"
import { cx, IconButton } from "../ui"
import { fileSize } from "../../lib/format"

/**
 * A deliberately small rich-text editor.
 *
 * Bold, italic, underline, two list styles and links - nothing else. Screenshots
 * pasted from the clipboard upload straight away and land inline; other files
 * attach as a chip below the surface. Whatever gets typed is sanitised again on
 * the server, so the allow-list here is a convenience rather than a control.
 *
 * Ctrl+Enter (Cmd+Enter on a Mac) calls `onSubmit` - post the reply, save the
 * edit. The toolbar is out of the Tab order: Tab from the field above lands in
 * the text, and the formatting has Ctrl+B, I and U, the files paste and drop.
 *
 * Inline images show with the viewer's media token so they load, but what the
 * editor hands back never carries one: the stored body keeps the bare URL.
 *
 * `document.execCommand` is formally deprecated but remains the only thing every
 * browser implements consistently for contentEditable. Replacing it means
 * adopting a full editor framework, which this prototype does not need.
 */

/** Past this, a quiet count appears so the server's limit is no surprise. */
const COUNTER_FROM = 45_000

function escapeAttribute(value) {
	return String(value).replace(/&/g, "&amp;").replace(/"/g, "&quot;").replace(/</g, "&lt;")
}

export function RichTextEditor({
	projectId,
	issueId,
	value,
	onChange,
	attachments,
	onAttachmentsChange,
	placeholder = "Write a reply...",
	minHeight = "min-h-[92px]",
	autoFocus = false,
	disabled = false,
	onSubmit
}) {
	const surfaceRef = useRef(null)
	const fileInputRef = useRef(null)
	const [uploading, setUploading] = useState(0)
	const [active, setActive] = useState({})
	const { toast, refreshUser, user } = useApp()
	// The value this editor last handed out. The surface holds the same body
	// with media tokens added, so comparing the two directly would always
	// differ and reset the caret on every keystroke.
	const lastEmitted = useRef(null)
	// Uploads finish one at a time, each after its own render. Appending to
	// the `attachments` prop would append to a stale copy and drop the others.
	const attachmentsRef = useRef(attachments)
	attachmentsRef.current = attachments

	const maxUploadBytes = user?.usage?.max_upload_bytes ?? user?.plan?.max_upload_bytes ?? -1

	// Only write into the DOM when the value comes from outside - a reset after
	// posting, or a body loaded for editing.
	useEffect(() => {
		const surface = surfaceRef.current
		if (!surface || value === lastEmitted.current) return
		const next = withMediaTokens(value || "")
		if (surface.innerHTML !== next) surface.innerHTML = next
		lastEmitted.current = value
	}, [value])

	useEffect(() => {
		if (autoFocus) surfaceRef.current?.focus()
	}, [autoFocus])

	// New lines as <p>, not the <div> browsers default to: the server's
	// allow-list has no <div>, so lines would run together on save.
	useEffect(() => {
		try {
			document.execCommand("defaultParagraphSeparator", false, "p")
		} catch {
			/* not every engine knows the command; the server converts as well */
		}
	}, [])

	const emit = useCallback(() => {
		const html = stripMediaTokens(surfaceRef.current?.innerHTML || "")
		lastEmitted.current = html
		onChange(html)
	}, [onChange])

	const setAttachments = useCallback(
		(next) => {
			attachmentsRef.current = next
			onAttachmentsChange(next)
		},
		[onAttachmentsChange]
	)

	const refreshActiveMarks = useCallback(() => {
		if (typeof document.queryCommandState !== "function") return
		try {
			setActive({
				bold: document.queryCommandState("bold"),
				italic: document.queryCommandState("italic"),
				underline: document.queryCommandState("underline"),
				ul: document.queryCommandState("insertUnorderedList"),
				ol: document.queryCommandState("insertOrderedList")
			})
		} catch {
			/* not all engines answer for every command */
		}
	}, [])

	const run = useCallback(
		(command, argument) => {
			surfaceRef.current?.focus()
			document.execCommand(command, false, argument)
			refreshActiveMarks()
			emit()
		},
		[emit, refreshActiveMarks]
	)

	const uploadOne = useCallback(
		async (file, { inline }) => {
			if (!projectId) return null
			if (maxUploadBytes >= 0 && file.size > maxUploadBytes) {
				toast(
					`${file.name || "That file"} is ${fileSize(file.size)} - your plan allows ${fileSize(
						maxUploadBytes
					)} per file.`,
					"error"
				)
				return null
			}
			setUploading((n) => n + 1)
			try {
				const { attachment } = await uploadFile(projectId, file, issueId)
				// Storage usage moved, so the quota meter needs the new number.
				refreshUser()

				if (inline && attachment.is_image) {
					surfaceRef.current?.focus()
					// Shown with a token so it loads here; `emit` strips it again.
					document.execCommand("insertHTML", false,
						`<img src="${escapeAttribute(mediaUrl(attachment.url))}" ` +
							`alt="${escapeAttribute(attachment.filename)}" ` +
							`data-attachment-id="${escapeAttribute(attachment.attachment_id)}" />`
					)
					emit()
				}
				setAttachments([...attachmentsRef.current, attachment])
				return attachment
			} catch (error) {
				toast(error.message || `Could not upload ${file.name}`, "error")
				return null
			} finally {
				setUploading((n) => n - 1)
			}
		},
		[projectId, issueId, maxUploadBytes, setAttachments, emit, toast, refreshUser]
	)

	const onPaste = useCallback(
		(event) => {
			const items = Array.from(event.clipboardData?.items || [])
			const images = items
				.filter((item) => item.kind === "file" && item.type.startsWith("image/"))
				.map((item) => item.getAsFile())
				.filter(Boolean)

			if (images.length) {
				event.preventDefault()
				images.forEach((file) => uploadOne(file, { inline: true }))
				return
			}

			// Paste as plain text: pasted formatting from Word or a mail client
			// otherwise drags a whole stylesheet into the body.
			const text = event.clipboardData?.getData("text/plain")
			if (text) {
				event.preventDefault()
				document.execCommand("insertText", false, text)
				emit()
			}
		},
		[uploadOne, emit]
	)

	const onKeyDown = useCallback(
		(event) => {
			const mod = event.metaKey || event.ctrlKey
			if (!mod) return
			if (event.key === "Enter") {
				// Without onSubmit, left to bubble: a form around the editor
				// handles it instead.
				if (onSubmit) {
					event.preventDefault()
					emit()
					onSubmit()
				}
				return
			}
			const key = event.key.toLowerCase()
			if (key === "b") {
				event.preventDefault()
				run("bold")
			} else if (key === "i") {
				event.preventDefault()
				run("italic")
			} else if (key === "u") {
				event.preventDefault()
				run("underline")
			}
		},
		[run, emit, onSubmit]
	)

	const onPickFiles = useCallback(
		(event) => {
			Array.from(event.target.files || []).forEach((file) =>
				uploadOne(file, { inline: file.type.startsWith("image/") })
			)
			event.target.value = ""
		},
		[uploadOne]
	)

	const onDrop = useCallback(
		(event) => {
			const files = Array.from(event.dataTransfer?.files || [])
			if (!files.length) return
			event.preventDefault()
			files.forEach((file) =>
				uploadOne(file, { inline: file.type.startsWith("image/") })
			)
		},
		[uploadOne]
	)

	const addLink = useCallback(() => {
		const href = window.prompt("Link URL")
		if (!href) return
		if (!/^https?:\/\//i.test(href)) {
			toast("Links need to start with http:// or https://", "error")
			return
		}
		run("createLink", href)
	}, [run, toast])

	const tools = [
		{ key: "bold", label: "Bold", icon: Bold, run: () => run("bold"), on: active.bold },
		{ key: "italic", label: "Italic", icon: Italic, run: () => run("italic"), on: active.italic },
		{
			key: "underline",
			label: "Underline",
			icon: Underline,
			run: () => run("underline"),
			on: active.underline
		},
		{ divider: true, key: "d1" },
		{
			key: "ul",
			label: "Bulleted list",
			icon: List,
			run: () => run("insertUnorderedList"),
			on: active.ul
		},
		{
			key: "ol",
			label: "Numbered list",
			icon: ListOrdered,
			run: () => run("insertOrderedList"),
			on: active.ol
		},
		{ key: "link", label: "Add link", icon: Link2, run: addLink }
	]

	return (
		<div
			className={cx(
				"rounded-lg border bg-white transition-colors",
				// One soft border that deepens on focus. No ring: a second
				// rectangle inside the first is what made this look boxy.
				"border-ink-200 focus-within:border-brand-400",
				disabled && "opacity-60"
			)}
		>
			<div className="flex flex-wrap items-center gap-0.5 border-b border-ink-100 px-1.5 py-1">
				{tools.map((tool) =>
					tool.divider ? (
						<span key={tool.key} className="mx-1 h-4 w-px bg-ink-200" aria-hidden />
					) : (
						<button
							key={tool.key}
							type="button"
							tabIndex={-1}
							disabled={disabled}
							aria-label={tool.label}
							aria-pressed={Boolean(tool.on)}
							title={tool.label}
							onMouseDown={(event) => event.preventDefault()}
							onClick={tool.run}
							className={cx(
								"inline-flex h-7 w-7 items-center justify-center rounded-md transition-colors",
								tool.on
									? "bg-brand-100 text-brand-800"
									: "text-ink-500 hover:bg-ink-100 hover:text-ink-900"
							)}
						>
							<tool.icon size={14} aria-hidden />
						</button>
					)
				)}

				<span className="mx-1 h-4 w-px bg-ink-200" aria-hidden />

				<button
					type="button"
					tabIndex={-1}
					disabled={disabled}
					onClick={() => fileInputRef.current?.click()}
					className="inline-flex h-7 items-center gap-1.5 rounded-md px-2 text-2xs font-medium text-ink-500 transition-colors hover:bg-ink-100 hover:text-ink-900"
				>
					<Paperclip size={13} aria-hidden />
					Attach
				</button>

				{uploading > 0 && (
					<span className="ml-auto inline-flex items-center gap-1.5 px-2 text-2xs text-ink-500">
						<Loader2 size={12} className="animate-spin" aria-hidden />
						Uploading
					</span>
				)}

				{(value?.length || 0) > COUNTER_FROM && (
					<span
						className={cx(
							"px-2 text-2xs tabular",
							uploading === 0 && "ml-auto",
							value.length > BODY_LIMIT ? "font-medium text-rose-600" : "text-ink-400"
						)}
						title="The longest body that can be saved"
					>
						{value.length.toLocaleString()} / {BODY_LIMIT.toLocaleString()}
					</span>
				)}
			</div>

			<div
				ref={surfaceRef}
				role="textbox"
				aria-multiline="true"
				aria-label={placeholder}
				contentEditable={!disabled}
				suppressContentEditableWarning
				data-placeholder={placeholder}
				onInput={emit}
				onBlur={emit}
				onPaste={onPaste}
				onKeyDown={onKeyDown}
				onKeyUp={refreshActiveMarks}
				onMouseUp={refreshActiveMarks}
				onDrop={onDrop}
				onDragOver={(event) => event.preventDefault()}
				className={cx(
					"editor-surface prose-note max-h-[420px] w-full overflow-y-auto px-3 py-2.5 scrollbar-slim",
					// The wrapper already shows focus; the global focus ring
					// would draw a second box around the text area itself.
					"outline-none focus:outline-none focus-visible:outline-none focus-visible:ring-0",
					minHeight
				)}
			/>

			{attachments.length > 0 && (
				<div className="flex flex-wrap gap-1.5 border-t border-ink-100 px-3 py-2">
					{attachments.map((file) => (
						<span
							key={file.attachment_id}
							className="inline-flex max-w-[16rem] items-center gap-1.5 rounded-md bg-ink-100 px-2 py-1 text-2xs text-ink-700"
						>
							<Paperclip size={11} className="shrink-0 text-ink-400" aria-hidden />
							<span className="truncate font-medium">{file.filename}</span>
							<span className="shrink-0 text-ink-400">{fileSize(file.size_bytes)}</span>
							<IconButton
								label={`Remove ${file.filename}`}
								icon={X}
								size={11}
								className="-mr-1 h-5 w-5"
								onClick={() =>
									setAttachments(
										attachmentsRef.current.filter(
											(a) => a.attachment_id !== file.attachment_id
										)
									)
								}
							/>
						</span>
					))}
				</div>
			)}

			<input
				ref={fileInputRef}
				type="file"
				multiple
				className="hidden"
				onChange={onPickFiles}
			/>
		</div>
	)
}
